import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Target, Lightbulb, Award } from 'lucide-react';
import Spline from '@splinetool/react-spline';
import { companyInfo, services, experienceHighlights } from '../mock/data';

const Homepage = () => {
  return (
    <div className="dark-theme">
      {/* Hero Section */}
      <section className="min-h-screen pt-24 flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <h1 className="display-huge text-white leading-tight">
                Blending AI with{' '}
                <span className="text-bluemp-400">Integrity</span>
              </h1>
              <p className="body-large text-gray-300 max-w-lg">
                {companyInfo.extendedTagline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  Book Discovery Call
                  <ArrowRight size={20} />
                </Link>
                <Link to="/solutions" className="btn-secondary">
                  Explore Solutions
                </Link>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex items-center gap-8 pt-8">
                <div className="text-center">
                  <div className="heading-2 text-bluemp-400">10M+</div>
                  <div className="body-small text-gray-400">Customers Reached</div>
                </div>
                <div className="text-center">
                  <div className="heading-2 text-bluemp-400">100%</div>
                  <div className="body-small text-gray-400">Ethical Compliance</div>
                </div>
                <div className="text-center">
                  <div className="heading-2 text-bluemp-400">Multi-Million</div>
                  <div className="body-small text-gray-400">Revenue Impact</div>
                </div>
              </div>
            </div>

            {/* Right Content - 3D Scene */}
            <div className="relative">
              <div style={{ width: "700px", height: "700px", overflow: "visible", position: "relative" }}>
                <Spline scene={process.env.REACT_APP_SPLINE_SCENE_URL} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four Pillars Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Built on Four Pillars
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Our foundation ensures every AI solution delivers measurable value while maintaining the highest standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Shield, title: "Trust", description: "Transparent processes and human accountability in every AI decision" },
              { icon: Lightbulb, title: "Innovation", description: "Cutting-edge AI solutions tailored to your unique business challenges" },
              { icon: Award, title: "Integrity", description: "Ethical AI practices that respect privacy and promote fairness" },
              { icon: Target, title: "Reliability", description: "Consistent, measurable results with ongoing support and optimization" }
            ].map((pillar, index) => (
              <div key={index} className="text-center group dark-hover p-8 rounded-lg bg-gray-900/50 backdrop-blur-sm">
                <pillar.icon size={48} className="text-bluemp-400 mx-auto mb-4" />
                <h3 className="heading-3 text-white mb-4">{pillar.title}</h3>
                <p className="body-small text-gray-300">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Intelligent AI Solutions That Deliver Results
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              From strategy to implementation, we provide comprehensive AI solutions tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service) => (
              <div key={service.id} className="group dark-hover p-8 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-bluemp-400/20">
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {service.title}
                </h3>
                <p className="body-small text-gray-300 mb-6 line-clamp-3">
                  {service.description}
                </p>
                <Link 
                  to={`/solutions/${service.id}`}
                  className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors"
                >
                  Learn More <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/solutions" className="btn-primary">
              View All Solutions
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Experience Highlights */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Proven Experience. Trusted Delivery.
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              We deliver data-driven AI solutions with measurable results across financial services, insurance, and communications sectors.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {experienceHighlights.map((highlight, index) => (
              <div key={index} className="text-center">
                <div className="display-large text-bluemp-400 mb-2">{highlight.metric}</div>
                <div className="body-medium text-gray-300">{highlight.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Every AI journey starts with a conversation. Let's discuss how we can help you achieve your goals with integrity and innovation.
          </p>
          <Link to="/contact" className="btn-primary text-xl px-12 py-6">
            Book Your Discovery Call Today
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Homepage;